import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import { isServer } from '@/components/shared/utility/browser';

const DATA_HASH_ATTR = 'data-hash';
const DATA_FULLSCREEN_ATTR = 'data-section-fullscreen';

// Utility to detect if the user is on a mobile device
const isMobileDevice = () =>
  /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

// Utility to detect LINE browser
// const isLineBrowser = () => /Line\/|Line/i.test(navigator.userAgent);

// Utility to detect Facebook browser
// const isFacebookBrowser = () =>
//   /FBAN|FBAV|FB_IAB|FB4A/i.test(navigator.userAgent);

// Utility function to calculate the position of the section based on the hash and offset
export const getSectionPosition = (hash: string, offset: number) => {
  const sectionElement = document.querySelector(
    `[${DATA_HASH_ATTR}="${hash}"]`
  );
  if (!sectionElement) return { top: -1, bottom: -1 };

  const rect = sectionElement.getBoundingClientRect();
  return {
    top: Math.ceil(rect.top + window.scrollY - offset),
    bottom: Math.ceil(rect.bottom + window.scrollY - offset - 2),
  };
};

// Function to scroll to a section based on the hash and an optional offset
export const scrollToSectionByHash = (
  hash: string | undefined,
  behavior: ScrollBehavior = 'auto',
  offset: number = 0
) => {
  if (isServer || !hash) return;

  if (hash === '#') {
    window.scrollTo({ top: 0, behavior });
    return;
  }

  const sectionElement = document.querySelector(
    `[${DATA_HASH_ATTR}="${hash}"]`
  );

  if (!sectionElement) return;

  const isFullscreen =
    sectionElement.getAttribute(DATA_FULLSCREEN_ATTR) === 'true';

  const adjustedOffset = isFullscreen ? 0 : offset;
  const { top } = getSectionPosition(hash, adjustedOffset);

  if (top >= 0) {
    window.scrollTo({ top, behavior });
  }
};

// Hash mapping for sections
const hashAnchorMap: Record<string, string> = {
  '#speed-interior': '#interior',
  '#hilander-interior': '#interior',
  '#speed-exterior': '#exterior',
  '#hilander-exterior': '#exterior',
  '#exterior-rs': '#exterior',
  '#exterior-ultimate': '#exterior',
  '#interior-rs': '#interior',
  '#interior-ultimate': '#interior',
};

// Utility to extract model name from hash
const extractModelName = (model: string): string => {
  return model
    .replace(/^#/, '') // Remove leading '#'
    .replace(/(^exterior-|^interior-)/, '') // Remove 'exterior-' or 'interior-' prefix
    .replace(/(-exterior$|-interior$)/, ''); // Remove '-exterior' or '-interior' suffix
};

// Function to handle scrolling to a section by hash
const handleScrollToHash = (
  hash: string,
  offset: number = 0,
  onTabChange?: (model: string) => void
) => {
  if (!hash) return;

  const anchor = hashAnchorMap[hash] ?? hash;

  if (onTabChange) {
    const strippedHash = extractModelName(hash);
    onTabChange(strippedHash);
  }

  scrollToSectionByHash(anchor, 'auto', offset);
};

// Utility to get the tab index based on model
export const findTabIndexFromModel = (
  modelParam: string,
  tabs: Array<{ value: string }> = []
): number => {
  if (!tabs) return 0;
  const foundIndex = tabs.findIndex((tab) => tab.value === modelParam);
  return foundIndex > -1 ? foundIndex : 0;
};

// Hook to handle scrolling and hash changes based on URL parameters
export const useScrollByParamsAndHash = (
  offset: number = 0,
  onTabChange?: (model: string) => void,
  resetTab?: () => void
) => {
  const searchParams = useSearchParams();

  useEffect(() => {
    if (isServer) return;

    const handleHashChangeAndLoad = () => {
      const modelParam = searchParams?.get('model');
      const sectionParam = searchParams?.get('section');
      const hash = window.location.hash;

      // Handle Tab Change or Reset
      if (modelParam && onTabChange) {
        onTabChange(modelParam);
      } else if (!modelParam && !hash && resetTab) {
        resetTab();
      }

      // Handle Query Param Scrolling
      if (sectionParam) {
        const getDelay = () => {
          if (!isMobileDevice()) {
            return 1000;
          }
          return 1200;
        };
        const delay = getDelay();
        setTimeout(
          () => scrollToSectionByHash(`#${sectionParam}`, 'auto', offset),
          delay
        );
      }

      // Handle Hash Scrolling
      if (hash) {
        const getDelay = () => {
          if (!isMobileDevice()) {
            return 1000;
          }
          return 1200;
        };

        const delay = getDelay();
        setTimeout(() => {
          if (onTabChange) {
            handleScrollToHash(hash, offset, onTabChange);
          } else {
            scrollToSectionByHash(hash, 'auto', offset);
          }
        }, delay);
      }
    };

    handleHashChangeAndLoad();

    window.addEventListener('hashchange', handleHashChangeAndLoad);
    window.addEventListener('popstate', handleHashChangeAndLoad);
    window.addEventListener('load', handleHashChangeAndLoad);

    return () => {
      window.removeEventListener('hashchange', handleHashChangeAndLoad);
      window.removeEventListener('popstate', handleHashChangeAndLoad);
      window.removeEventListener('load', handleHashChangeAndLoad);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
};
